import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { Plus, FilePdf, ArrowsClockwise, PencilSimple, Trash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import ArticleSelector from '../components/ArticleSelector';
import { downloadBlob } from '../utils/download';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  accepted: { label: 'Accepté', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700' },
  converted: { label: 'Converti', color: 'bg-blue-100 text-blue-700' }
};

const Quotes = () => {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [keys, setKeys] = useState([]);
  const [itProducts, setItProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [printing, setPrinting] = useState([]);
  const [businessCards, setBusinessCards] = useState([]);
  const [binding, setBinding] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingQuote, setEditingQuote] = useState(null);

  const [quoteForm, setQuoteForm] = useState({
    client_id: '',
    items: [],
    notes: '',
    additional_info: '',
    validity_days: 30
  });

  const [itemMode, setItemMode] = useState('product');
  const [selectedProductKey, setSelectedProductKey] = useState('');
  const [currentItem, setCurrentItem] = useState({
    type: '',
    description: '',
    quantity: '1',
    unit_price: '',
  });

  useEffect(() => {
    fetchQuotes();
    fetchClients();
    fetchKeys();
    fetchItProducts();
    fetchServices();
    fetchTariffs();
    fetchPrestations();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/quotes`, { withCredentials: true });
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
      setClients(response.data);
    } catch (error) { console.error(error); }
  };

  const fetchKeys = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/keys`, { withCredentials: true });
      setKeys(response.data);
    } catch (error) { console.error(error); }
  };

  const fetchItProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/it-products`, { withCredentials: true });
      setItProducts(response.data);
    } catch (error) { console.error(error); }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services?only_active=true`, { withCredentials: true });
      setServices(response.data);
    } catch (error) { console.error(error); }
  };

  const fetchTariffs = async () => {
    try {
      const [pr, bc, bi] = await Promise.all([
        axios.get(`${API_URL}/api/printing`, { withCredentials: true }),
        axios.get(`${API_URL}/api/business-cards`, { withCredentials: true }),
        axios.get(`${API_URL}/api/binding`, { withCredentials: true }),
      ]);
      setPrinting(pr.data);
      setBusinessCards(bc.data);
      setBinding(bi.data);
    } catch (error) { console.error(error); }
  };

  const fetchPrestations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/prestations`, { withCredentials: true });
      setPrestations(response.data);
    } catch (error) { console.error(error); }
  };

  const addItemToQuote = () => {
    let newItem;
    
    if (itemMode === 'product') {
      if (!selectedProductKey) {
        toast.error('Veuillez sélectionner un produit');
        return;
      }
      
      const [productType, productId] = selectedProductKey.split(':');
      let product;
      if (productType === 'key') product = keys.find(k => k.id === productId);
      else if (productType === 'it_product') product = itProducts.find(p => p.id === productId);
      else if (productType === 'service') product = services.find(s => s.id === productId);
      else if (productType === 'printing') product = printing.find(p => p.id === productId);
      else if (productType === 'business_card') product = businessCards.find(p => p.id === productId);
      else if (productType === 'binding') product = binding.find(p => p.id === productId);
      else if (productType === 'prestation') product = prestations.find(p => p.id === productId);
      
      if (!product) return;
      
      const quantity = parseInt(currentItem.quantity);
      // Catalog prices are entered as TTC by convention (see Settings tooltip).
      // Convert to HT so the quote's "P.U. HT" column is truly HT.
      const vat = product.vat_rate !== undefined ? product.vat_rate : 0.085;
      const toHT = (ttc) => (vat > 0 ? +(ttc / (1 + vat)).toFixed(4) : ttc);
      let unitPrice, typeLabel, descLabel;
      if (productType === 'service') {
        unitPrice = toHT(product.unit_price);
        typeLabel = product.category || 'Service';
        descLabel = product.name + (product.description ? ` - ${product.description}` : '');
      } else if (productType === 'printing') {
        unitPrice = toHT(product.price);
        typeLabel = 'Impression';
        descLabel = `Impression ${product.format} - ${product.paper_type} - ${product.is_color ? 'Couleur' : 'Monochrome'}`;
      } else if (productType === 'business_card') {
        unitPrice = toHT(product.price);
        typeLabel = 'Carte de visite';
        descLabel = `Cartes de visite - Papier ${product.paper_type} - ${product.is_color ? 'Couleur' : 'Monochrome'}`;
      } else if (productType === 'binding') {
        unitPrice = toHT(product.price);
        typeLabel = 'Reliure';
        descLabel = `Reliure ${product.min_pages} à ${product.max_pages} pages`;
      } else if (productType === 'prestation') {
        unitPrice = toHT(product.price);
        typeLabel = 'Prestation';
        descLabel = product.name;
      } else {
        unitPrice = toHT(product.sale_price);
        typeLabel = productType === 'key' ? 'Clé' : 'Produit IT';
        descLabel = product.name;
        if (productType === 'key' && product.characteristics) descLabel += ` - ${product.characteristics}`;
        else if (productType === 'it_product' && product.category) descLabel += ` (${product.category})`;
      }
      
      newItem = {
        type: typeLabel,
        description: descLabel,
        quantity,
        unit_price: unitPrice,
        total_price: quantity * unitPrice,
        product_id: productId,
        product_type: productType,
        vat_rate: product.vat_rate !== undefined ? product.vat_rate : 0.085,
      };
    } else {
      if (!currentItem.description || !currentItem.unit_price) {
        toast.error('Veuillez remplir tous les champs');
        return;
      }
      
      const quantity = parseInt(currentItem.quantity);
      const unitPrice = parseFloat(currentItem.unit_price);
      
      newItem = {
        type: currentItem.type || 'Service',
        description: currentItem.description,
        quantity,
        unit_price: unitPrice,
        total_price: quantity * unitPrice,
        product_id: null,
        product_type: null
      };
    }

    setQuoteForm({ ...quoteForm, items: [...quoteForm.items, newItem] });
    setCurrentItem({ type: '', description: '', quantity: '1', unit_price: '' });
    setSelectedProductKey('');
  };

  const removeItem = (index) => {
    setQuoteForm({ ...quoteForm, items: quoteForm.items.filter((_, i) => i !== index) });
  };

  const computeLineTotal = (qty, unit, discountPercent) => {
    const base = (qty || 0) * (unit || 0);
    const pct = Math.max(0, Math.min(100, parseFloat(discountPercent) || 0));
    return Math.max(0, base * (1 - pct / 100));
  };

  const updateItemDiscount = (index, value) => {
    const newItems = quoteForm.items.map((it, i) => {
      if (i !== index) return it;
      const next = { ...it };
      const pct = Math.max(0, Math.min(100, parseFloat(value) || 0));
      next.discount_percent = pct;
      next.discount_type = pct > 0 ? 'percent' : null;
      next.discount_value = pct;
      next.total_price = computeLineTotal(next.quantity, next.unit_price, pct);
      return next;
    });
    setQuoteForm({ ...quoteForm, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (quoteForm.items.length === 0) {
      toast.error('Ajoutez au moins un article');
      return;
    }

    try {
      const payload = { ...quoteForm, validity_days: parseInt(quoteForm.validity_days) };
      if (editingQuote) {
        await axios.put(`${API_URL}/api/quotes/${editingQuote.id}`, payload, { withCredentials: true });
        toast.success('Devis mis à jour');
      } else {
        await axios.post(`${API_URL}/api/quotes`, payload, { withCredentials: true });
        toast.success('Devis créé');
      }
      setShowCreateDialog(false);
      resetForm();
      fetchQuotes();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur');
    }
  };

  const resetForm = () => {
    setQuoteForm({ client_id: '', items: [], notes: '', additional_info: '', validity_days: 30 });
    setCurrentItem({ type: '', description: '', quantity: '1', unit_price: '' });
    setSelectedProductKey('');
    setItemMode('product');
    setEditingQuote(null);
  };

  const openEditDialog = (quote) => {
    setEditingQuote(quote);
    setQuoteForm({
      client_id: quote.client_id,
      items: quote.items.map(item => ({ ...item, product_id: item.product_id || null, product_type: item.product_type || null })),
      notes: quote.notes || '',
      additional_info: quote.additional_info || '',
      validity_days: quote.validity_days || 30
    });
    setShowCreateDialog(true);
  };

  const downloadPDF = async (quoteId) => {
    try {
      const response = await axios.get(`${API_URL}/api/quotes/${quoteId}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      downloadBlob(response.data, `devis_${quoteId}.pdf`, 'application/pdf');
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const convertToInvoice = async (quote) => {
    if (!(await confirm(`Convertir le devis ${quote.quote_number} en facture ? Le stock sera décrémenté.`))) return;
    try {
      const response = await axios.post(`${API_URL}/api/quotes/${quote.id}/convert-to-invoice`, {}, { withCredentials: true });
      toast.success(`Facture ${response.data.invoice_number} créée`);
      fetchQuotes();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la conversion';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur');
    }
  };

  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/quotes/${quoteId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Statut mis à jour: ${STATUS_LABELS[newStatus]?.label || newStatus}`);
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (quoteId) => {
    if (!(await confirm('Êtes-vous sûr de vouloir supprimer ce devis ?'))) return;
    try {
      await axios.delete(`${API_URL}/api/quotes/${quoteId}`, { withCredentials: true });
      toast.success('Devis supprimé');
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formTotals = quoteForm.items.reduce(
    (acc, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const unit = parseFloat(item.unit_price) || 0;
      const pct = Math.max(0, Math.min(100, parseFloat(item.discount_percent) || 0));
      const vat = parseFloat(item.vat_rate) || 0;
      const lineHT = qty * unit * (1 - pct / 100);
      acc.ht += lineHT;
      acc.vat += lineHT * vat;
      return acc;
    },
    { ht: 0, vat: 0 },
  );
  const quoteTotalHT = formTotals.ht;
  const quoteTotalVAT = formTotals.vat;
  const quoteTotalTTC = quoteTotalHT + quoteTotalVAT;
  const quoteDisplayVatRate = quoteForm.items.length
    ? (parseFloat(quoteForm.items[0].vat_rate) || 0.085)
    : 0.085;

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="quotes-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Devis</h1>
            <p className="text-slate-600">Créez et gérez vos devis clients</p>
          </div>
          <Button
            onClick={() => { resetForm(); setShowCreateDialog(true); }}
            data-testid="create-quote-btn"
            className="bg-orange-500 hover:bg-orange-400 text-white"
          >
            <Plus size={20} weight="bold" className="mr-2" />
            Nouveau devis
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : (
          <>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Rechercher par numéro ou client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-quotes-input"
                className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Numéro</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Client</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Montant</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Validité</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Statut</th>
                      <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Date</th>
                      <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes
                      .filter(q => !searchQuery || 
                        q.quote_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        q.client_name.toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .map((quote) => {
                      const status = STATUS_LABELS[quote.status] || { label: quote.status, color: 'bg-slate-100 text-slate-700' };
                      const isConverted = quote.status === 'converted';
                      return (
                        <tr key={quote.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 text-sm text-slate-900 font-medium">{quote.quote_number}</td>
                          <td className="py-3 px-4 text-sm text-slate-900">{quote.client_name}</td>
                          <td className="py-3 px-4 text-sm text-slate-900">{quote.total_amount.toFixed(2)} €</td>
                          <td className="py-3 px-4 text-sm text-slate-600">{quote.validity_days} jours</td>
                          <td className="py-3 px-4 text-sm">
                            {isConverted ? (
                              <span className={`inline-block px-2 py-1 rounded-full text-xs ${status.color}`}>
                                {status.label}
                              </span>
                            ) : (
                              <Select
                                value={quote.status}
                                onValueChange={(v) => updateQuoteStatus(quote.id, v)}
                              >
                                <SelectTrigger
                                  data-testid={`status-select-${quote.id}`}
                                  className={`w-32 h-7 text-xs px-2 py-0 border-0 ${status.color}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">En attente</SelectItem>
                                  <SelectItem value="accepted">Accepté</SelectItem>
                                  <SelectItem value="rejected">Refusé</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">
                            {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <button
                              onClick={() => downloadPDF(quote.id)}
                              data-testid={`download-quote-pdf-${quote.id}`}
                              className="inline-flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-md mr-1"
                              title="Télécharger PDF"
                            >
                              <FilePdf size={18} />
                            </button>
                            {!isConverted && (
                              <button
                                onClick={() => convertToInvoice(quote)}
                                data-testid={`convert-quote-${quote.id}`}
                                className="inline-flex items-center justify-center p-2 text-green-600 hover:bg-green-50 rounded-md mr-1"
                                title="Convertir en facture"
                              >
                                <ArrowsClockwise size={18} />
                              </button>
                            )}
                            {!isConverted && (
                              <button
                                onClick={() => openEditDialog(quote)}
                                data-testid={`edit-quote-${quote.id}`}
                                className="inline-flex items-center justify-center p-2 text-orange-600 hover:bg-orange-50 rounded-md mr-1"
                                title="Modifier"
                              >
                                <PencilSimple size={18} />
                              </button>
                            )}
                            {user?.role === 'admin' && (
                              <button
                                onClick={() => handleDelete(quote.id)}
                                data-testid={`delete-quote-${quote.id}`}
                                className="inline-flex items-center justify-center p-2 text-red-700 hover:bg-red-50 rounded-md"
                                title="Supprimer"
                              >
                                <Trash size={18} />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {quotes.length === 0 && (
                      <tr>
                        <td colSpan="7" className="py-12 text-center text-slate-500">
                          Aucun devis trouvé
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Create/Edit Quote Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-quote-dialog">
            <DialogHeader>
              <DialogTitle>{editingQuote ? `Modifier ${editingQuote.quote_number}` : 'Créer un devis'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
                  <Select
                    value={quoteForm.client_id}
                    onValueChange={(value) => setQuoteForm({ ...quoteForm, client_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="quote-client-select">
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Validité (jours)</label>
                  <Input
                    type="number"
                    value={quoteForm.validity_days}
                    onChange={(e) => setQuoteForm({ ...quoteForm, validity_days: e.target.value })}
                    data-testid="quote-validity-input"
                    min="1"
                    max="365"
                  />
                </div>
              </div>

              {/* Items Section */}
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">Ajouter des articles</h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setItemMode('product')}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors duration-200 ${
                        itemMode === 'product' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 border border-slate-300'
                      }`}
                    >
                      Produit
                    </button>
                    <button
                      type="button"
                      onClick={() => setItemMode('custom')}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors duration-200 ${
                        itemMode === 'custom' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 border border-slate-300'
                      }`}
                    >
                      Personnalisé
                    </button>
                  </div>
                </div>
                
                {itemMode === 'product' ? (
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-9">
                      <ArticleSelector
                        value={selectedProductKey}
                        onValueChange={setSelectedProductKey}
                        keys={keys}
                        itProducts={itProducts}
                        services={services}
                        printing={printing}
                        businessCards={businessCards}
                        binding={binding}
                        prestations={prestations}
                        testId="quote-item-product-select"
                        allowZeroStock
                      />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" placeholder="Qté" value={currentItem.quantity}
                        onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })} min="1" />
                    </div>
                    <div className="col-span-1">
                      <Button type="button" onClick={addItemToQuote} className="bg-blue-600 hover:bg-blue-500 text-white w-full">
                        <Plus size={20} />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-3">
                      <Input placeholder="Type" value={currentItem.type}
                        onChange={(e) => setCurrentItem({ ...currentItem, type: e.target.value })} />
                    </div>
                    <div className="col-span-4">
                      <Input placeholder="Description" value={currentItem.description}
                        onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })} />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" placeholder="Qté" value={currentItem.quantity}
                        onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })} min="1" />
                    </div>
                    <div className="col-span-2">
                      <Input type="number" step="0.01" placeholder="Prix" value={currentItem.unit_price}
                        onChange={(e) => setCurrentItem({ ...currentItem, unit_price: e.target.value })} />
                    </div>
                    <div className="col-span-1">
                      <Button type="button" onClick={addItemToQuote} className="bg-blue-600 hover:bg-blue-500 text-white w-full">
                        <Plus size={20} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {quoteForm.items.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-200">
                        <th className="text-left py-2 px-3 text-xs uppercase text-slate-600">Description</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">Qté</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">P.U. HT</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">Remise %</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">P.U. HT remisé</th>
                        <th className="text-right py-2 px-2 text-xs uppercase text-slate-600">Total HT</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {quoteForm.items.map((item, index) => {
                        const pct = Math.max(0, Math.min(100, parseFloat(item.discount_percent) || 0));
                        const unitAfter = (parseFloat(item.unit_price) || 0) * (1 - pct / 100);
                        return (
                          <tr key={index} className="border-b border-slate-100">
                            <td className="py-2 px-3 text-slate-900">
                              <div className="text-slate-500 text-xs">{item.type}</div>
                              {item.description}
                            </td>
                            <td className="py-2 px-2 text-slate-900 text-right">{item.quantity}</td>
                            <td className="py-2 px-2 text-slate-900 text-right">{(parseFloat(item.unit_price) || 0).toFixed(2)} €</td>
                            <td className="py-2 px-2 text-right">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={pct}
                                onChange={(e) => updateItemDiscount(index, e.target.value)}
                                data-testid={`quote-item-discount-percent-${index}`}
                                className="w-16 px-1 py-1 text-xs border border-slate-300 rounded text-right"
                              />
                            </td>
                            <td className="py-2 px-2 text-slate-700 text-right">{unitAfter.toFixed(2)} €</td>
                            <td className="py-2 px-2 text-slate-900 text-right font-medium">{item.total_price.toFixed(2)} €</td>
                            <td className="py-2 px-2 text-right">
                              <button type="button" onClick={() => removeItem(index)} className="text-red-600">×</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 flex flex-col items-end gap-1" data-testid="quote-totals-summary">
                    <div className="w-72 flex justify-between text-slate-700">
                      <span>Total HT</span>
                      <span data-testid="quote-total-ht" className="font-medium">{quoteTotalHT.toFixed(2)} €</span>
                    </div>
                    <div className="w-72 flex justify-between text-slate-700">
                      <span>TVA {(quoteDisplayVatRate * 100).toFixed(1).replace('.0', '')}%</span>
                      <span data-testid="quote-total-vat" className="font-medium">{quoteTotalVAT.toFixed(2)} €</span>
                    </div>
                    <div className="w-72 flex justify-between border-t border-slate-300 pt-1 mt-1 text-slate-900 font-bold text-lg">
                      <span>Total TTC</span>
                      <span data-testid="quote-total-ttc">{quoteTotalTTC.toFixed(2)} €</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Informations complémentaires</label>
                <textarea
                  value={quoteForm.additional_info}
                  onChange={(e) => setQuoteForm({ ...quoteForm, additional_info: e.target.value })}
                  data-testid="quote-additional-info-input"
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Informations supplémentaires à afficher sur le devis..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes internes</label>
                <Input
                  value={quoteForm.notes}
                  onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
                  data-testid="quote-notes-input"
                  placeholder="Notes..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Annuler
                </Button>
                <Button type="submit" data-testid="submit-quote-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
                  {editingQuote ? 'Mettre à jour' : 'Créer le devis'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Quotes;
